import type { BattleScript, BuyRoundScript, NaturalRoundScript, SpinScript } from "./TeddyRoundBuilder";

/** A grid from rows of space-separated ids, top row first. */
export function rows(text: string): string[][] {
  return text
    .trim()
    .split("\n")
    .map((row) => row.trim().split(/\s+/));
}

const battle = (column: number, top: number, bottom: number, winner: BattleScript["winner"]): BattleScript => ({ column, top, bottom, winner });

/**
 * Dead grids: reel 1 holds only high symbols and reel 2 only low ones, so no line runs past its first reel (the server's
 * buy-reveal trick), with no W, VS or FS. The free spins between the scripted moments land these.
 */
const DEAD: readonly SpinScript[] = [
  { grid: rows(`H1 L1 L5 H2 L4 \n H2 L2 H4 L1 H3 \n H3 L3 L2 H3 L1 \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
  { grid: rows(`H3 L4 H2 L1 H4 \n H1 L5 L3 H4 L2 \n H4 L1 H1 L5 H3 \n H2 L3 L4 H2 L1 \n H3 L2 H4 L3 H2`) },
  { grid: rows(`H2 L3 L1 H3 L5 \n H4 L1 H2 L2 H1 \n H1 L5 L4 H4 L3 \n H3 L2 H3 L1 H4 \n H2 L4 L2 H1 L1`) },
];
const dead = (i: number): SpinScript => DEAD[i % DEAD.length]!;

/** The base game's rounds. */
export const BASE_SCRIPTS = {
  reel_no_win: {
    id: "reel-no-win",
    base: { grid: rows(`L4 H2 L5 W L2 \n H3 L1 H4 L2 L5 \n L1 H4 FS L3 H1 \n H2 L3 L4 H1 L2 \n L5 W L2 H3 L4`) },
  },
  reel_line_win: {
    id: "reel-line-win",
    // H4 × 3 on the middle row.
    base: { grid: rows(`H1 L1 L5 H2 L4 \n H2 L2 H1 L1 H3 \n H4 H4 H4 L3 L1 \n H3 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
  },
  reel_three_wins: {
    id: "reel-three-wins",
    // Three separate wins: L2 × 4 on the top row, H3 × 3 on the middle row, L5 × 5 on the bottom row.
    base: { grid: rows(`L2 L2 L2 L2 H4 \n H1 L1 H2 L3 L4 \n H3 H3 H3 L1 L2 \n H2 L3 L4 H4 H1 \n L5 L5 L5 L5 L5`) },
  },
  reel_grouped_win: {
    id: "reel-grouped-win",
    // H2 × 5 on the second row and H2 × 5 on the zigzag below it share three cells: one highlight of two lines.
    base: { grid: rows(`H1 L1 L4 L3 H4 \n H2 H2 H2 H2 H2 \n H3 H2 L2 H2 H1 \n H4 L3 L5 L1 L3 \n H1 L2 L1 L4 L5`) },
  },
  reel_zigzag: {
    id: "reel-zigzag",
    // H1 × 5 on the zigzag through rows 1 and 2 (cells 0, 6, 2, 8, 4).
    base: { grid: rows(`H1 L1 H1 L3 H1 \n L2 H1 L4 H1 L5 \n H3 L5 L2 L1 L4 \n H2 L3 H4 L5 H3 \n H4 L2 L3 H2 L1`) },
  },
  reel_wild_win: {
    id: "reel-wild-win",
    // A wild completes H3 × 4 on the bottom row.
    base: { grid: rows(`H1 L1 L5 H2 L4 \n H2 L2 H4 L1 H3 \n H4 L3 L2 H3 L1 \n H4 H1 L4 L1 H2 \n H3 W H3 H3 L5`) },
  },
  reel_vs_battle: {
    id: "reel-vs-battle",
    // A VS on reel 3; L1 × 5 on the middle row crosses it: its battle plays (3 vs 10, the top wins: × 3).
    base: { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H4 L4 H3 \n L1 L1 VS L1 L1 \n H4 L4 H1 L5 H2 \n H3 L5 L3 H1 L2`), battles: [battle(2, 3, 10, "top")] },
  },
  reel_vs_double: {
    id: "reel-vs-double",
    // VS on reels 2 and 4; L2 × 5 on the top row and on the zigzag through it cross both: the battles play left to right
    // and their winners add (2 + 3 = × 5).
    base: { grid: rows(`L2 VS L2 VS L2 \n H1 L3 H4 L4 H3 \n H3 L4 L1 L5 H2 \n H2 L1 L4 L2 H4 \n H4 L5 L3 H1 L1`), battles: [battle(1, 2, 3, "top"), battle(3, 3, 4, "top")] },
  },
  reel_vs_no_battle: {
    id: "reel-vs-no-battle",
    // A VS lands on reel 5 but the only win (L3 × 3, top row) breaks on reel 4: no battle plays, the VS stays.
    base: { grid: rows(`L3 L3 L3 H2 L4 \n H2 L1 H4 L5 H3 \n H4 L4 L2 H3 VS \n H3 L2 H1 L4 H2 \n H1 L5 L5 H1 L2`), battles: [battle(4, 5, 9, "top")] },
  },
  reel_anticipation: {
    id: "reel-anticipation",
    // Scatters on reels 1 and 2: one short of the trigger, so reels 3–5 tease; none lands. No win.
    base: { grid: rows(`FS L1 L5 H2 L4 \n H2 L2 H4 L1 H3 \n H3 FS L2 H3 L1 \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
  },
  reel_nice_win: {
    id: "reel-nice-win",
    // H4 × 5 on the middle row with a VS battle won at 2 on reel 5: 12 × 2 = 24× — the "nice" tier.
    base: { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H4 H4 H4 H4 VS \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(4, 2, 3, "top")] },
  },
  reel_big_win: {
    id: "reel-big-win",
    // H4 × 5 on the middle row through a VS battle won at 7: 12 × 7 = 84× — escalates nice → big → epic (MEGA WIN art).
    base: { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H4 H4 VS H4 H4 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 7, 9, "top")] },
  },
  reel_huge_win: {
    id: "reel-huge-win",
    // H4 × 5 on the top row and on the zigzag through it, across two battles won at 6 and 5 (2 × 12 × 11 = 264×), and
    // H2 × 4 across them: 281× — up to "huge".
    base: { grid: rows(`H4 VS H4 VS H4 \n H1 L3 H2 L4 H3 \n H3 L4 L1 L5 H2 \n H2 L1 L4 L2 H1 \n H1 L5 L3 H1 L1`), battles: [battle(1, 5, 6, "bottom"), battle(3, 5, 9, "top")] },
  },
} satisfies Record<string, NaturalRoundScript>;

/** The free-spins sessions a base spin triggers. */
export const SESSION_SCRIPTS = {
  fs_vs_rush: {
    id: "fs-vs-rush",
    // Three scatters trigger VS Rush (8 spins); the trigger spin also pays L2 × 3 on the top row.
    base: { grid: rows(`L2 L2 L2 FS L4 \n H2 L1 FS L1 H3 \n FS L3 L2 H3 L1 \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
    freeSpins: [
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n L1 L1 VS L1 L1 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 4, 6, "top")] },
      dead(0),
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H3 VS H3 VS L2 \n H4 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(1, 2, 3, "top"), battle(3, 5, 6, "bottom")] },
      dead(1),
      { grid: rows(`L4 L4 L4 L4 H2 \n H2 L3 H1 VS H3 \n H3 L1 L2 H3 L1 \n H4 L5 H1 L5 H2 \n H1 L2 L3 H1 L5`), battles: [battle(3, 3, 4, "top")] },
      dead(2),
      { grid: rows(`H2 H2 H2 H1 L4 \n H1 L3 L5 L4 H3 \n H3 L1 L2 H3 L1 \n H4 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`) },
      dead(0),
    ],
  },
  fs_super_bonus: {
    id: "fs-super-bonus",
    // Four scatters trigger the Super Bonus (10 spins): the saved total ratchets 4 → 6 → 9, every later battle opens on
    // it, and the last spin collects it.
    base: { grid: rows(`FS L1 L5 H2 L4 \n H2 FS H4 L1 H3 \n H3 L3 FS H3 L1 \n H4 L4 H1 FS H2 \n H1 L5 L3 H1 L5`) },
    freeSpins: [
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n L3 L3 VS L3 L2 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 4, 7, "top")] },
      dead(1),
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H2 VS H2 H2 L1 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(1, 4, 6, "bottom")] },
      dead(2),
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n L4 L4 L4 VS L3 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(3, 6, 9, "bottom")] },
      dead(0),
      // A VS that no winning line crosses: its battle is drawn, never shown, and saves nothing.
      { grid: rows(`H3 L2 L5 H2 VS \n H2 L3 H1 L4 H3 \n H4 L1 L2 H3 L1 \n H1 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(4, 9, 20, "bottom")] },
      dead(1),
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H4 H4 VS H4 L1 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 9, 10, "top")] },
      dead(2),
    ],
  },
  fs_retrigger: {
    id: "fs-retrigger",
    // VS Rush whose third spin lands three scatters: +5 spins (13 in all).
    base: { grid: rows(`FS L1 L5 H2 L4 \n H2 L2 FS L1 H3 \n H3 L3 L2 H3 FS \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
    freeSpins: [
      dead(0),
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n L5 L5 VS L5 L2 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 3, 5, "top")] },
      { grid: rows(`FS L1 L5 H2 L4 \n H2 L2 H4 FS H3 \n H3 L3 FS H3 L1 \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
      dead(1),
      dead(2),
      dead(0),
      dead(1),
      dead(2),
      dead(0),
      dead(1),
      dead(2),
      { grid: rows(`H2 H2 H2 H2 L4 \n H1 L3 L5 L4 H3 \n H3 L1 L2 H3 L1 \n H4 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`) },
      dead(0),
    ],
  },
  fs_capped: {
    id: "fs-capped",
    // A Super Bonus whose second spin turns every reel wild: every line pays H4 × 5 × the summed winners, past the
    // 25,000× cap. The session ends there: the meter shows only what is left under the cap; the lines keep their own.
    base: { grid: rows(`FS L1 L5 H2 L4 \n H2 FS H4 L1 H3 \n H3 L3 FS H3 L1 \n H4 L4 H1 FS H2 \n H1 L5 L3 H1 L5`) },
    freeSpins: [
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H4 H4 VS H4 H4 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 50, 75, "bottom")] },
      { grid: rows(`VS VS VS VS VS \n H2 L3 H1 L4 H3 \n H4 L1 L2 H3 L1 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(0, 75, 100, "bottom"), battle(1, 75, 100, "bottom"), battle(2, 75, 100, "bottom"), battle(3, 75, 100, "bottom"), battle(4, 75, 100, "bottom")] },
    ],
  },
} satisfies Record<string, NaturalRoundScript>;

/** The bought sessions: the synthetic reveal, then the spins. */
export const BUY_SCRIPTS = {
  buy_vs_rush: {
    id: "buy-vs-rush",
    session: "vsRush",
    scatterCells: [5, 12, 23],
    freeSpins: [
      dead(0),
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H4 VS H4 L1 L1 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(1, 5, 8, "top")] },
      dead(1),
      dead(2),
      { grid: rows(`L1 L1 L1 VS L4 \n H2 L3 H1 L4 H3 \n H4 L5 L2 H3 L1 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(3, 2, 4, "top")] },
      dead(0),
      dead(1),
      dead(2),
    ],
  },
  buy_super_bonus: {
    id: "buy-super-bonus",
    session: "superBonus",
    scatterCells: [0, 7, 13, 21],
    freeSpins: [
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n L2 L2 VS L2 L4 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 5, 6, "top")] },
      dead(0),
      dead(1),
      { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n H3 H3 VS H3 L1 \n H4 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(2, 5, 8, "bottom")] },
      dead(2),
      dead(0),
      dead(1),
      dead(2),
      dead(0),
      dead(1),
    ],
  },
} satisfies Record<string, BuyRoundScript>;

/** The feature modes' base spins. */
export const MODE_SCRIPTS = {
  bonus_hunt_tease: {
    id: "bonus-hunt-tease",
    mode: "bonus_hunt_mode",
    // Two scatters by reel 2 tease reels 3–5; no third lands. No win.
    base: { grid: rows(`H1 FS L5 H2 L4 \n FS L2 H4 L1 H3 \n H3 L3 L2 H3 L1 \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
  },
  bonus_hunt_trigger: {
    id: "bonus-hunt-trigger",
    mode: "bonus_hunt_mode",
    base: { grid: rows(`FS L1 L5 H2 L4 \n H2 FS H4 L1 H3 \n H3 L3 L2 FS L1 \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`) },
    freeSpins: [dead(0), dead(1), dead(2), dead(0), { grid: rows(`H4 H4 H4 L1 L4 \n H2 L3 H1 L4 H3 \n H3 L1 L2 H3 L1 \n H1 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`) }, dead(1), dead(2), dead(0)],
  },
  guaranteed_vs_battle: {
    id: "guaranteed-vs-battle",
    mode: "guaranteed_vs_mode",
    // The mode's VS lands on reel 1 and L2 × 4 runs from it: its battle plays.
    base: { grid: rows(`H1 L2 L5 H2 L4 \n H2 L3 H1 L4 H3 \n VS L2 L2 L2 H1 \n H3 L4 H1 L5 H2 \n H1 L5 L3 H1 L2`), battles: [battle(0, 2, 5, "top")] },
  },
  guaranteed_vs_no_battle: {
    id: "guaranteed-vs-no-battle",
    mode: "guaranteed_vs_mode",
    // The mode guarantees the VS lands, not that it battles: no winning line crosses it.
    base: { grid: rows(`H1 L1 L5 H2 L4 \n H2 L2 H4 L1 H3 \n H3 L3 L2 H3 VS \n H4 L4 H1 L4 H2 \n H1 L5 L3 H1 L5`), battles: [battle(4, 3, 6, "bottom")] },
  },
} satisfies Record<string, NaturalRoundScript>;
